import type {
  BuildSessionReportInput,
  SessionReportFinding,
  SessionReportSummary,
} from "./types";
import { SESSION_REPORT_THRESHOLDS } from "./summary";

const partialDuplicateIndexes = (input: BuildSessionReportInput): number[] => {
  const seen = new Map<string, number>();
  const duplicates: number[] = [];
  for (let index = 0; index < input.auditState.finalDigests.length; index += 1) {
    const digest = input.auditState.finalDigests[index].digest;
    if (seen.has(digest)) {
      duplicates.push(index);
      continue;
    }
    seen.set(digest, index);
  }
  return duplicates;
};

const finding = (
  code: SessionReportFinding["code"],
  severity: SessionReportFinding["severity"],
  message: string
): SessionReportFinding => ({
  code,
  severity,
  message,
});

export const deriveSessionReportFindings = (
  input: BuildSessionReportInput,
  summary: SessionReportSummary
): SessionReportFinding[] => {
  const findings: SessionReportFinding[] = [];

  if ((summary.terminalDisconnectCount ?? 0) > 0) {
    findings.push(
      finding(
        "terminal_disconnect_observed",
        summary.outcome === "failed" ? "critical" : "warning",
        "A terminal disconnect was observed."
      )
    );
  }

  if (summary.outcome === "degraded_unrecovered") {
    findings.push(
      finding(
        "buffer_not_drained_after_recovery",
        "critical",
        "Buffered audio was not fully drained after recovery."
      )
    );
  }

  if (
    summary.drainExitReason === "stalled" ||
    summary.finishDeliveryState === "timed_out" ||
    (summary.bufferStallCount ?? 0) > 0
  ) {
    findings.push(
      finding("drain_stalled", "critical", "Audio draining stalled or timed out.")
    );
  }

  const duplicateFinals = partialDuplicateIndexes(input);
  if (duplicateFinals.length > 0) {
    findings.push(
      finding(
        "duplicate_final_transcripts",
        "warning",
        "Duplicate final transcripts were observed."
      )
    );
  }

  if (
    summary.p95FirstPartialLatencyMs !== null &&
    summary.p95FirstPartialLatencyMs >= SESSION_REPORT_THRESHOLDS.partialLatencyMs
  ) {
    findings.push(
      finding(
        "high_partial_latency",
        "warning",
        "Partial transcript latency exceeded the threshold."
      )
    );
  }

  if (
    summary.p95FinalLatencyMs !== null &&
    summary.p95FinalLatencyMs >= SESSION_REPORT_THRESHOLDS.finalLatencyMs
  ) {
    findings.push(
      finding(
        "high_final_latency",
        "warning",
        "Final transcript latency exceeded the threshold."
      )
    );
  }

  if (
    summary.p95RecoveryDurationMs !== null &&
    summary.p95RecoveryDurationMs >= SESSION_REPORT_THRESHOLDS.recoveryDurationMs
  ) {
    findings.push(
      finding("slow_recovery", "warning", "Recovery latency exceeded the threshold.")
    );
  }

  if (
    summary.peakBufferedAudioBytes !== null &&
    summary.peakBufferedAudioBytes >= SESSION_REPORT_THRESHOLDS.peakBufferedAudioBytes
  ) {
    findings.push(
      finding(
        "high_peak_buffer",
        "warning",
        "Peak buffered audio exceeded the threshold."
      )
    );
  }

  if ((summary.flapCount ?? 0) >= SESSION_REPORT_THRESHOLDS.offlineFlaps) {
    findings.push(
      finding(
        "offline_flaps_detected",
        "info",
        "Offline browser activity was observed."
      )
    );
  }

  if ((summary.clippingEventCount ?? 0) > 0) {
    findings.push(
      finding("audio_clipping_detected", "warning", "Audio clipping was observed.")
    );
  }

  if ((summary.audioCallbackGapCount ?? 0) > 0) {
    findings.push(
      finding(
        "audio_callback_gaps_detected",
        "warning",
        "Audio callback gaps were observed."
      )
    );
  }

  return findings;
};
